<template>
  <div class="dual-list-select" :class="{ 'dual-list-select--compact': compact }">
    <div class="dual-list-select__panel">
      <span class="dual-list-select__title">{{ availableTitle }}</span>
      <input
        v-model="filterAvailable"
        type="text"
        class="dual-list-select__filter"
        :placeholder="searchPlaceholder"
      />
      <ul class="dual-list-select__list">
        <li
          v-for="option in filteredAvailableOptions"
          :key="option.value"
          class="dual-list-select__item"
          @click="selectOption(option.value)"
        >
          {{ option.label }}
        </li>
        <li v-if="!filteredAvailableOptions.length" class="dual-list-select__empty">
          Nenhum item disponível
        </li>
      </ul>
    </div>

    <div class="dual-list-select__actions">
      <button
        type="button"
        class="dual-list-select__action-btn"
        title="Selecionar todos"
        :disabled="!filteredAvailableOptions.length"
        @click="selectAllFiltered"
      >
        &gt;&gt;
      </button>
      <button
        type="button"
        class="dual-list-select__action-btn"
        title="Remover todos"
        :disabled="!selectedOrder.length"
        @click="unselectAll"
      >
        &lt;&lt;
      </button>
    </div>

    <div class="dual-list-select__panel">
      <span class="dual-list-select__title">{{ selectedTitle }}</span>
      <ul class="dual-list-select__list">
        <draggable
          v-if="selectedOrder.length"
          v-model="selectedOrder"
          tag="div"
          class="dual-list-select__draggable"
          @end="emitSelectedOrder"
        >
          <li
            v-for="value in selectedOrder"
            :key="value"
            class="dual-list-select__item dual-list-select__item--selected"
            @click="unselectOption(value)"
          >
            {{ optionsMap[value] ? optionsMap[value].label : value }}
          </li>
        </draggable>
        <li v-else class="dual-list-select__empty">
          Nenhum item selecionado
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  name: 'DualListSelect',
  components: {
    draggable,
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
    availableTitle: {
      type: String,
      default: 'Disponíveis',
    },
    selectedTitle: {
      type: String,
      default: 'Selecionados',
    },
    searchPlaceholder: {
      type: String,
      default: 'Pesquisar',
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filterAvailable: '',
      selectedOrder: [],
    };
  },
  computed: {
    optionsMap() {
      return this.options.reduce((map, option) => {
        map[option.value] = option;
        return map;
      }, {});
    },
    availableOptions() {
      return this.options.filter((option) => !this.value.includes(option.value));
    },
    filteredAvailableOptions() {
      return this.filterOptions(this.availableOptions, this.filterAvailable);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        const currentValues = this.selectedOrder.filter((item) => newValue.includes(item));
        const newItems = newValue.filter((item) => !currentValues.includes(item));
        this.selectedOrder = [...currentValues, ...newItems];
      },
    },
  },
  methods: {
    filterOptions(options, term) {
      if (!term) {
        return options;
      }

      const normalizedTerm = term.toLowerCase();
      return options.filter((option) => option.label.toLowerCase().includes(normalizedTerm));
    },
    selectOption(value) {
      if (this.value.includes(value)) {
        return;
      }

      this.$emit('input', [...this.value, value]);
    },
    unselectOption(value) {
      this.$emit('input', this.value.filter((item) => item !== value));
    },
    selectAllFiltered() {
      const valuesToAdd = this.filteredAvailableOptions.map((option) => option.value);
      this.$emit('input', [...this.value, ...valuesToAdd]);
    },
    unselectAll() {
      this.$emit('input', []);
    },
    emitSelectedOrder() {
      this.$emit('input', [...this.selectedOrder]);
    },
  },
};
</script>

<style scoped>
.dual-list-select {
  display: flex;
  gap: 0.75rem;
  align-items: stretch;
}

.dual-list-select__panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.dual-list-select__title {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  font-size: 0.95rem;
}

.dual-list-select__filter {
  width: 100%;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: var(--input-border, 1px solid rgba(0, 0, 0, 0.15));
  border-radius: var(--medium-border-radius, 6px);
  font-size: 0.9rem;
}

.dual-list-select__list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: var(--input-border, 1px solid rgba(0, 0, 0, 0.15));
  border-radius: var(--medium-border-radius, 6px);
  min-height: 180px;
  max-height: 220px;
  overflow-y: auto;
}

.dual-list-select__draggable {
  display: flex;
  flex-direction: column;
}

.dual-list-select__item {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.15s ease;
}

.dual-list-select__item:last-child {
  border-bottom: none;
}

.dual-list-select__item:hover {
  background-color: rgba(16, 109, 196, 0.08);
}

.dual-list-select__item--selected {
  color: var(--primary-color, #106dc4);
  background-color: rgba(16, 109, 196, 0.05);
}

.dual-list-select__empty {
  padding: 0.75rem;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.5);
}

.dual-list-select__actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
}

.dual-list-select__action-btn {
  border: 1px solid rgba(0, 0, 0, 0.15);
  background-color: #fff;
  border-radius: var(--medium-border-radius, 6px);
  width: 2.25rem;
  height: 2.25rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--primary-color, #106dc4);
}

.dual-list-select__action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dual-list-select__action-btn:not(:disabled):hover {
  background-color: rgba(16, 109, 196, 0.08);
}

.dual-list-select--compact {
  gap: 0.5rem;
}

.dual-list-select--compact .dual-list-select__title {
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
}

.dual-list-select--compact .dual-list-select__filter {
  margin-bottom: 0.35rem;
  padding: 0.35rem 0.5rem;
  font-size: 0.8rem;
}

.dual-list-select--compact .dual-list-select__list {
  min-height: 90px;
  max-height: 110px;
}

.dual-list-select--compact .dual-list-select__item {
  padding: 0.35rem 0.5rem;
  font-size: 0.8rem;
}

.dual-list-select--compact .dual-list-select__empty {
  padding: 0.5rem;
  font-size: 0.8rem;
}

.dual-list-select--compact .dual-list-select__action-btn {
  width: 1.75rem;
  height: 1.75rem;
  font-size: 0.75rem;
}
</style>
